import React, { useEffect, useRef, useState } from "react";
import "./offerform.scss";
import BreadCrumsHeader from "../BreadCrums";
import OfferFormLayout from "./OfferFormLayout";
import CategorySelection from "./CategorySelection";
import SubCategorySelection from "./SubCategorySelection";
import OfferCreation from "./OfferCreation";
import CustomizeOffer from "./CustomizeOffer";
import RulesRegulations from "./RulesRegulations";
import Location from "./Location";
import OfferReview from "./OfferReview";
import MerchantSelection from "./MerchantSelection"; 
import GenericPopup from "../GenericPopup";
import PrimaryOutlineButton from "../PrimaryOutlineButton";
import InfoIcon from "../../../Assets/Icons/info.svg";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { store } from "../../../redux/store";
import {
  createOffer,
  getCategories,
  getOfferById,
  deleteOffer,
} from "../../../services/offerService";
import { resetOfferState } from "../../../redux/slices/offerSlice";
import { logout } from "../../../services/authService";
import { getFcmToken } from "../../../utils/connectStore";
import toast from "../../../utils/toast";

const OfferForm = ({ merchant }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { id = null } = location?.state || "";
  
  // Use local state instead of useSelector to avoid reducer execution conflicts
  const [role, setRole] = useState(null);
  const [offerData, setOfferData] = useState(null);
  const [reduxSelectedMerchant, setReduxSelectedMerchant] = useState(null);
  
  // Determine initial step based on role
  const [stepIndex, setStepIndex] = useState(1);
  const [subCategories, setSubCategories] = useState([]);
  const [categoryId, setCategoryId] = useState(undefined);
  const [payload, setPayload] = useState(null);
  const [categories, setCategories] = useState([]);
  const [editOfferData, setEditOfferData] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedMerchant, setSelectedMerchant] = useState(null); // For admin merchant selection
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [pendingNavigatePath, setPendingNavigatePath] = useState(null);
  const [pendingAction, setPendingAction] = useState("navigate");
  const topRef = useRef(null);

  // Safely update state from Redux store to avoid reducer execution conflicts
  useEffect(() => {
    const updateState = () => {
      try {
        const state = store.getState();
        const newRole = state?.authReducer?.tokenData?.user?.role;
        const newOfferData = state?.offerReducer?.createdOffer;
        const newSelectedMerchant = state?.merchantReducer?.selectedMerchent;
        
        setRole(newRole);
        setOfferData(newOfferData);
        setReduxSelectedMerchant(newSelectedMerchant);
      } catch (error) {
        console.warn('OfferForm store access error:', error.message);
      }
    };
    
    // Initial state update with delay to avoid reducer execution conflicts
    const timeoutId = setTimeout(updateState, 0);
    return () => clearTimeout(timeoutId);
  }, []);

  // Subscribe to store changes
  useEffect(() => {
    const unsubscribe = store.subscribe(() => {
      try {
        const state = store.getState();
        const newRole = state?.authReducer?.tokenData?.user?.role;
        const newOfferData = state?.offerReducer?.createdOffer;
        const newSelectedMerchant = state?.merchantReducer?.selectedMerchent;
        
        setRole(prev => {
          if (prev !== newRole) return newRole;
          return prev;
        });
        setOfferData(prev => {
          if (prev !== newOfferData) return newOfferData;
          return prev;
        });
        setReduxSelectedMerchant(prev => {
          if (prev !== newSelectedMerchant) return newSelectedMerchant;
          return prev;
        });
      } catch (error) {
        console.warn('OfferForm store subscription error:', error.message);
      }
    });
    
    return unsubscribe;
  }, []);

  // Update stepIndex when role changes
  useEffect(() => {
    if (role === "admin") {
      setStepIndex(1);
    }
  }, [role]);

  // Helper function to get step numbers based on role
  const getStepNumber = (baseStep) => {
    return role === "admin" ? baseStep + 1 : baseStep;
  };

  const getOfferData = async () => {
    const getPromise = await dispatch(getOfferById({ id: id }))?.unwrap();
    console.log(getPromise,"getPromise1234567890")
    if (getPromise?.success === true) {
      setEditOfferData(getPromise?.data);
    }
  };

  const getCategoriesData = async () => {
    const categoryPromise = await dispatch(getCategories())?.unwrap();
    if (categoryPromise?.success === true) {
      setCategories(categoryPromise?.data);
    }
  };

  useEffect(() => {
    getCategoriesData();
  }, []);

  useEffect(() => {
    if (id !== null) {
      setIsEdit(true);
      getOfferData();
    }
  }, [id]);

  // If admin is editing, skip Merchant selection step
  useEffect(() => {
    if (role === "admin" && isEdit && stepIndex === 1) {
      setStepIndex(2);
    }
  }, [role, isEdit]);

  const handleStepClick = (target) => {
    // Prevent navigating to Merchant selection when editing as admin
    if (role === "admin" && isEdit && target === 1) return;
    setStepIndex(target);
  };

  const offersPath = role === "admin" ? "/offer-list" : "/merchant/offer-list";

  const items = [
    {
      title: "Offers",
      href: offersPath,
    },
    {
      title: "Create an Offer",
    },
  ];

  const handlePrevious = () => {
    let previousStep = stepIndex - 1;
    // Prevent admin (editing) from going back to merchant selection
    if (role === "admin" && isEdit && previousStep === 1) {
      previousStep = 2;
    }
    if (previousStep < 1) {
      previousStep = 1;
    }
    setStepIndex(previousStep);
  };

  const setPayloadData = (data) => {
    setPayload(data);
  };

  const handleSaveAndExit = async () => {
    // For admin: do NOT leave drafts behind; delete any in-progress offer
    if (role === "admin" && !isEdit && offerData?._id) {
      try {
        // More robust merchant ID resolution for admin deletion
        const merchantIdForDelete = 
          selectedMerchant?._id || 
          reduxSelectedMerchant?._id || 
          offerData?.merchant?._id || 
          offerData?.merchantId ||
          payload?.merchantId;
        
        // Debug: confirm which merchantId is being used during cleanup
        console.log("[OfferForm] Save & Exit cleanup:", {
          selectedMerchantId: selectedMerchant?._id || null,
          reduxSelectedMerchantId: reduxSelectedMerchant?._id || null,
          offerMerchantId: offerData?.merchant?._id || null,
          offerMerchantIdDirect: offerData?.merchantId || null,
          payloadMerchantId: payload?.merchantId || null,
          merchantIdForDelete,
          offerId: offerData?._id || null
        });
        
        if (!merchantIdForDelete) {
          console.warn("[OfferForm] No merchant ID found for deletion, attempting without merchantId");
          // Try deletion without merchantId as fallback
          await dispatch(deleteOffer({ id: offerData._id }))?.unwrap();
        } else {
          await dispatch(deleteOffer({ id: offerData._id, merchantId: merchantIdForDelete }))?.unwrap();
        }
      } catch (error) {
        // Always proceed; the thunk normalizes Merchant-not-found to success now
        console.warn('Cleanup draft result:', error?.message || 'Handled');
      }
      toast.success("Your offer has been discarded.");
      navigate("/offer-list");
      return;
    }

    // For non-admin (or editing), keep existing draft behavior
    if (payload !== null) {
      const createPromise = await dispatch(createOffer({
        ...payload,
        offerId: isEdit ? editOfferData?._id : offerData?._id,
        isCreated: false,
        ...(role === "admin" && selectedMerchant && { merchantId: selectedMerchant._id })
      }))?.unwrap();
      if (createPromise?.success === true) {
        navigate("/merchant/offer-list");
      }
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    return () => {
      dispatch(resetOfferState());
    };
  }, []);

  useEffect(() => {
    if (topRef.current) {
      topRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [stepIndex]);

  // Reset offer state when merchant changes to prevent permission issues
  useEffect(() => {
    if (selectedMerchant && offerData?.merchant?._id !== selectedMerchant._id) {
      dispatch(resetOfferState());
    }
  }, [selectedMerchant, offerData?.merchant?._id, dispatch]);

  // Guard (admin any, merchant create): intercept back button and mark session for sidebar
  useEffect(() => {
    const shouldGuard = role === "admin" || (role === "merchant" && !isEdit);
    if (!shouldGuard) return;

    // Mark a session flag for sidebar/navigation interception
    sessionStorage.setItem("ADMIN_OFFER_CREATION_GUARD", "true");

    // Remove native beforeunload prompt; use custom modal instead
    const handleBeforeUnload = null;

    const handlePopState = () => {
      // push back to current page and show modal
      window.history.pushState(null, "", window.location.pathname);
      setShowLeaveConfirm(true);
      setPendingNavigatePath("/offer-list");
    };

    const handleAttemptExit = (evt) => {
      const targetPath = evt?.detail?.path || "/offer-list";
      setPendingNavigatePath(targetPath);
      if (evt?.detail?.action) {
        setPendingAction(evt.detail.action);
      } else {
        setPendingAction("navigate");
      }
      setShowLeaveConfirm(true);
    };

    // Do not attach native beforeunload to avoid browser toast
    window.history.pushState(null, "", window.location.pathname);
    window.addEventListener("popstate", handlePopState);
    window.addEventListener("attempt-exit-offer-form", handleAttemptExit);

    // Intercept refresh shortcuts (F5, Ctrl/Cmd+R) to show custom modal
    const keydownHandler = (e) => {
      const isRefreshKey =
        e.key === "F5" ||
        e.keyCode === 116 ||
        ((e.ctrlKey || e.metaKey) && (e.key.toLowerCase() === "r"));
      if (isRefreshKey) {
        e.preventDefault();
        setPendingAction("reload");
        setShowLeaveConfirm(true);
      }
    };
    window.addEventListener("keydown", keydownHandler, true);

    // Intercept Link (<a>) navigations globally
    const clickCapture = (e) => {
      const anchor = e.target.closest && e.target.closest('a[href]');
      if (!anchor) return;

      try {
        const href = anchor.getAttribute('href') || '';
        const hasDownload = anchor.hasAttribute('download');
        const bypass = anchor.getAttribute('data-bypass-exit-guard') === 'true';

        // Allow downloads and blob/object URLs without guard
        if (hasDownload || href.startsWith('blob:') || bypass) {
          return; // don't intercept
        }

        // Intercept in-app navigations only
        if (!href.startsWith('http')) {
          e.preventDefault();
          setPendingNavigatePath(href);
          setShowLeaveConfirm(true);
        }
      } catch (_) {}
    };
    document.addEventListener('click', clickCapture, true);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener("attempt-exit-offer-form", handleAttemptExit);
      window.removeEventListener("keydown", keydownHandler, true);
      document.removeEventListener('click', clickCapture, true);
      sessionStorage.removeItem("ADMIN_OFFER_CREATION_GUARD");
    };
  }, [role, isEdit]);

  return (
    <div ref={topRef} className="offerFormPage">
      {/* Step 1: Select Merchant (Admin only) */}
      {role === "admin" && !isEdit && stepIndex === 1 && (
        <OfferFormLayout
          title={null}
          breadcrumbs={<BreadCrumsHeader heading="Create an Offer" items={items} form={true} />}
          content={
            <MerchantSelection
              stepIndex={stepIndex}
              setStepIndex={setStepIndex}
              selectedMerchant={selectedMerchant}
              setSelectedMerchant={setSelectedMerchant}
              setPayloadData={setPayloadData}
              editOfferData={editOfferData}
              isEdit={isEdit}
            />
          }
          stepIndex={stepIndex}
          handleSaveAndExit={handleSaveAndExit}
        />
      )}

      {/* Category Selection */}
      {stepIndex === getStepNumber(1) && (
        <OfferFormLayout
          title={null}
          breadcrumbs={<BreadCrumsHeader heading="Create an Offer" items={items} form={true} />}
          content={
            <CategorySelection
              setSubCategories={setSubCategories}
              stepIndex={stepIndex}
              setStepIndex={setStepIndex}
              categoryId={categoryId}
              setCategoryId={setCategoryId}
              setPayloadData={setPayloadData}
              categories={categories}
              editOfferData={editOfferData}
              isEdit={isEdit}
              selectedMerchant={selectedMerchant}
              handlePrevious={handlePrevious}
              showPreviousButton={
                !(
                  merchant === true &&
                  role !== "admin" &&
                  stepIndex === getStepNumber(1)
                )
              }
            />
          }
          stepIndex={stepIndex}
          handleSaveAndExit={handleSaveAndExit}
          onStepClick={handleStepClick}
        />
      )}

      {/* Sub Category Selection */}
      {stepIndex === getStepNumber(2) && (
        <OfferFormLayout
          title={null}
          breadcrumbs={<BreadCrumsHeader heading="Create an Offer" items={items} form={true} />}
          content={
            <SubCategorySelection
              categoryId={categoryId}
              subCategories={subCategories}
              setSubCategories={setSubCategories}
              stepIndex={stepIndex}
              setStepIndex={setStepIndex}
              handlePrevious={handlePrevious}
              setPayloadData={setPayloadData}
              editOfferData={editOfferData}
              isEdit={isEdit}
              selectedMerchant={selectedMerchant}
              categories={categories}
            />
          }
          stepIndex={stepIndex}
          handleSaveAndExit={handleSaveAndExit}
          onStepClick={handleStepClick}
        />
      )}

      {/* Offer Creation */}
      {stepIndex === getStepNumber(3) && (
        <OfferFormLayout
          title={null}
          breadcrumbs={<BreadCrumsHeader heading="Create an Offer" items={items} form={true} />}
          content={
            <OfferCreation
              stepIndex={stepIndex}
              setStepIndex={setStepIndex}
              handlePrevious={handlePrevious}
              setPayloadData={setPayloadData}
              editOfferData={editOfferData}
              isEdit={isEdit}
              selectedMerchant={selectedMerchant}
            />
          }
          stepIndex={stepIndex}
          handleSaveAndExit={handleSaveAndExit}
          onStepClick={handleStepClick}
        />
      )}

      {/* Customize Offer */}
      {stepIndex === getStepNumber(4) && (
        <OfferFormLayout
          title={null}
          breadcrumbs={<BreadCrumsHeader heading="Create an Offer" items={items} form={true} />}
          content={
            <CustomizeOffer
              stepIndex={stepIndex}
              setStepIndex={setStepIndex}
              handlePrevious={handlePrevious}
              setPayloadData={setPayloadData}
              editOfferData={editOfferData}
              isEdit={isEdit}
              selectedMerchant={selectedMerchant}
            />
          }
          stepIndex={stepIndex}
          handleSaveAndExit={handleSaveAndExit}
          onStepClick={handleStepClick}
        />
      )}

      {/* Rules and Regulations */}
      {stepIndex === getStepNumber(5) && (
        <OfferFormLayout
          title={null}
          breadcrumbs={<BreadCrumsHeader heading="Create an Offer" items={items} form={true} />}
          content={
            <RulesRegulations
              stepIndex={stepIndex}
              setStepIndex={setStepIndex}
              handlePrevious={handlePrevious}
              setPayloadData={setPayloadData}
              editOfferData={editOfferData}
              isEdit={isEdit}
              selectedMerchant={selectedMerchant}
            />
          }
          stepIndex={stepIndex}
          handleSaveAndExit={handleSaveAndExit}
          onStepClick={handleStepClick}
        />
      )}

      {/* Location (for INSTORE_SWOODLE_OFFER only) */}
      {stepIndex === getStepNumber(6) && offerData?.offerType === "INSTORE_SWOODLE_OFFER" && (
        <OfferFormLayout
          title={null}
          breadcrumbs={<BreadCrumsHeader heading="Create an Offer" items={items} form={true} />}
          content={
            <Location
              stepIndex={stepIndex}
              setStepIndex={setStepIndex}
              handlePrevious={handlePrevious}
              setPayloadData={setPayloadData}
              editOfferData={editOfferData}
              isEdit={isEdit}
              selectedMerchant={selectedMerchant}
            />
          }
          stepIndex={stepIndex}
          handleSaveAndExit={handleSaveAndExit}
          onStepClick={handleStepClick}
        />
      )}

      {/* Review and Approve */}
      {((stepIndex === getStepNumber(7) && offerData?.offerType === "INSTORE_SWOODLE_OFFER") || 
        (stepIndex === getStepNumber(6) && offerData?.offerType !== "INSTORE_SWOODLE_OFFER")) && (
        <OfferFormLayout
          title={null}
          breadcrumbs={<BreadCrumsHeader heading="Create an Offer" items={items} form={true} />}
          content={
            <OfferReview
              handlePrevious={handlePrevious}
              editOfferData={editOfferData}
              setPayloadData={setPayloadData}
              selectedMerchant={selectedMerchant}
            />
          }
          stepIndex={stepIndex}
          handleSaveAndExit={handleSaveAndExit}
          onStepClick={handleStepClick}
        />
      )}
      {showLeaveConfirm && (
        <GenericPopup
          icon={InfoIcon}
          content={"Are you sure you want to leave this offer? Your progress will be discarded."}
          popupClassName="w-[335px] sm:w-[470px] pb-8"
          cancelBtn={
            <PrimaryOutlineButton
              label="No"
              className="sm:w-[30%] md:w-[35%] lg:w-[35%] font-poppins sm:text-[12px] md:text-[14px] text-center font-[500] sm:h-[35px] md:h-[40px] rounded-[10px] mt-5"
              onClick={() => setShowLeaveConfirm(false)}
            />
          }
          continueBtn={
            <PrimaryOutlineButton
              label="Yes"
              className="sm:w-[30%] md:w-[35%] lg:w-[35%] font-poppins sm:text-[12px] md:text-[14px] text-center font-[500] sm:h-[35px] md:h-[40px] rounded-[10px] mt-5 bg-red-500 hover:bg-red-600 text-white border-red-500"
              onClick={async () => {
                setShowLeaveConfirm(false);
                sessionStorage.removeItem("ADMIN_OFFER_CREATION_GUARD");
                if (pendingAction === "logout") {
                  // Store the user role before logout since it will be cleared
                  const currentUserRole = role;
                  
                  const result = await dispatch(logout({ fcmToken: getFcmToken() }))?.unwrap();
                  if (result?.success) {
                    // Redirect to fundraiser login page if user is a fundraiser, otherwise main login
                    if (currentUserRole && currentUserRole.toLowerCase() === "fundraiser") {
                      navigate("/fundraiser/account-login");
                    } else {
                      navigate("/");
                    }
                  } else {
                    // Redirect to fundraiser login page if user is a fundraiser, otherwise main login
                    if (currentUserRole && currentUserRole.toLowerCase() === "fundraiser") {
                      navigate("/fundraiser/account-login");
                    } else {
                      navigate("/");
                    }
                  }
                } else {
                  // Admin leaving create flow: delete draft instead of leaving it
                  if (role === "admin" && !isEdit && offerData?._id) {
                    try {
                      // More robust merchant ID resolution for admin deletion
                      const merchantIdForDelete = 
                        selectedMerchant?._id || 
                        reduxSelectedMerchant?._id || 
                        offerData?.merchant?._id || 
                        offerData?.merchantId ||
                        payload?.merchantId;
                      
                      // Debug: confirm which merchantId is being used on guarded exit
                      console.log("[OfferForm] Guarded exit cleanup:", {
                        selectedMerchantId: selectedMerchant?._id || null,
                        reduxSelectedMerchantId: reduxSelectedMerchant?._id || null,
                        offerMerchantId: offerData?.merchant?._id || null,
                        offerMerchantIdDirect: offerData?.merchantId || null,
                        payloadMerchantId: payload?.merchantId || null,
                        merchantIdForDelete,
                        offerId: offerData?._id || null
                      });
                      
                      if (!merchantIdForDelete) {
                        console.warn("[OfferForm] No merchant ID found for deletion, attempting without merchantId");
                        // Try deletion without merchantId as fallback
                        const res = await dispatch(deleteOffer({ id: offerData._id }))?.unwrap();
                        if (res?.success) {
                          toast.success("Your offer has been discarded.");
                        } else {
                          toast.success("Your offer has been discarded.");
                        }
                      } else {
                        const res = await dispatch(deleteOffer({ id: offerData._id, merchantId: merchantIdForDelete }))?.unwrap();
                        if (res?.success) {
                          toast.success("Your offer has been discarded.");
                        } else {
                          toast.success("Your offer has been discarded.");
                        }
                      }
                    } catch (error) {
                      // If deletion fails (e.g., "Merchant not found"), just continue - the draft will remain but admin won't see it
                      console.warn('Could not delete offer draft:', error?.message || 'Unknown error');
                      toast.success("Your offer has been discarded.");
                    }
                  }
                  navigate(pendingNavigatePath || "/offer-list");
                }
              }}
            />
          }
        />
      )}
    </div>
  );
};

export default OfferForm;